/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

/**
 * Tabbable Mode helpers
 */
import { head } from 'ramda';
import { last } from 'ramda';
import { pipe } from 'ramda';

// definition for Element types that can receive focus
export type FocusableElement = HTMLElement | SVGElement;

import { getClientHints } from '../PRIVATE_clientHints';

/**
 * Query selector to return all tabbable elements
 * @param allowAllTabIndex This param is optional. The default value is false. To include the element with tabIndex='-1', specify allowAllTabIndex to true.
 */
const findTabbableQuery = (allowAllTabIndex?: boolean) => {
  const defaultFocusableElements = [
    'button',
    '[href]',
    'input',
    'select',
    'textarea',
    '[tabindex]',
    'video'
  ];
  const selectorSuffix = ':not([tabindex="-1"]):not([disabled]):not([hidden])';

  const elementsCount = defaultFocusableElements.length;
  let safeFocusablesSelector = '';

  // Loop adding suffix
  for (let i = 0; i < elementsCount; i++) {
    const elSelector = `${defaultFocusableElements[i]}${selectorSuffix}`;
    safeFocusablesSelector += `${elSelector}, `;
  }

  // Special cases
  safeFocusablesSelector += "[contenteditable]:not([contenteditable='false'])";
  if (allowAllTabIndex) {
    safeFocusablesSelector += ', [tabIndex]';
  }
  return safeFocusablesSelector;
};

/**
 * Use tabbableQuery selector to determine if an element is tabbable
 * @param element the element
 * @param allowAllTabIndex This param is optional. The default value is false. To include the element with tabIndex='-1', specify allowAllTabIndex to true
 */
const isTabbableElement = (element: FocusableElement, allowAllTabIndex?: boolean) =>
  element.matches(findTabbableQuery(allowAllTabIndex));

/**
 * All tabbable child elements in a node.
 * @param el the node
 * @param includeItself This param is optional. The default value is false. To include the current node, specify includeItself to true
 * @param allowAllTabIndex This param is optional. The default value is false. To include the element with tabIndex='-1', specify allowAllTabIndex to true.
 */
const allTabbableElements = (
  el: FocusableElement,
  includeItself?: boolean,
  allowAllTabIndex?: boolean
) => {
  const tabbableQuery = findTabbableQuery(allowAllTabIndex);
  const tabbable = Array.from(el.querySelectorAll(tabbableQuery)).filter((element) => {
    return !isHidden(element as HTMLElement);
  }) as FocusableElement[];

  if (includeItself && isTabbableElement(el, allowAllTabIndex)) {
    tabbable.push(el);
  }
  return tabbable;
};

// All tabbable child elements in a node,  or the container itself if there is none
const tabbablesOrContainer = (element: FocusableElement) => {
  const tabbableElements = allTabbableElements(element);
  return tabbableElements.length ? tabbableElements : [element];
};

// First tabbable child element in a node, or the container itself if there is none
const firstTabbableOrContainer = pipe(tabbablesOrContainer, head);

/**
 * Focus Helpers
 */

// preventScroll safe focus setter
const focusOn = (element: FocusableElement | { focus: () => void }) => {
  element.focus({ preventScroll: true });
};

// focus on first tabbable in a container or container itself if there is none
const focusWithin = (element: FocusableElement) => {
  const firstTabbable = firstTabbableOrContainer(element);
  focusOn(firstTabbable as FocusableElement);
};

// focus on initial tabstop
const focusOnStart = pipe(tabbablesOrContainer, head, focusOn);

// focus on last tabstop
const focusOnEnd = pipe(tabbablesOrContainer, last, focusOn);

// get active element from document parent of the node
const getActiveElement = (node?: Node) => {
  const conditionalDocument = node?.ownerDocument ?? document;
  return conditionalDocument.activeElement as FocusableElement;
};

// get active element from document parent of the node
const getBodyElement = (node?: Node) => {
  const conditionalDocument = node?.ownerDocument ?? document;
  return conditionalDocument.body;
};

const isHidden = (element: HTMLElement): boolean => {
  // 1.A Case: display: 'none' | position: 'fixed' (except Firefox)
  // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
  // Note: offsetParent returns null in the following situations:
  // The element or any ancestor has the display property set to none.
  // The element has the position property set to fixed (Firefox returns <body>).
  // ...
  // To check the latter - whether the element has fixed position
  // we would have to call expensive getComputedStyle()
  // As we have not seen a fixed position tabbable element yet
  // we intentionally don't do that until we got a usecase.
  // Most hidden elements are hidden using display none which
  // can be checked cheap using following:
  if (element.offsetParent === null) {
    return true;
  }

  // 2. Case: visibility: 'hidden'
  // There's no other way than calling getComputedStyle():
  // getComputedStyle() correctly computes visibility even it is inherited.
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/getComputedStyle
  // The Window.getComputedStyle() method returns an object containing the values of all CSS properties of an element,
  // after applying active stylesheets and resolving any basic computation those values may contain.
  const elComputedStyles = window.getComputedStyle(element);
  if (elComputedStyles.visibility === 'hidden') {
    return true;
  }

  // 1.B Firefox case (see comment of 1.A):
  // "The element has the position property set to fixed (Firefox returns <body>)."
  // But in case element's parent has 'translate' css style applied, which is a typical
  // residual style left on an element after an animation effect, Firefox does not
  // report document.body as the offsetParent.
  // Therefore we eliminate fixed positioned elements from the tabbable array with a direct check:
  const browser = getClientHints().browser;
  if (browser === 'firefox' && elComputedStyles.position === 'fixed') {
    return true;
  }
  // Element is not hidden
  return false;
};

const getAutofocusFocusables = (element: HTMLElement): HTMLElement[] => {
  const selector = '[autofocus]:not([tabindex="-1"]):not([disabled]):not([hidden])';
  const focusableCandidates: HTMLElement[] = Array.from(element.querySelectorAll(selector));
  const focusables: HTMLElement[] = focusableCandidates.filter((item: HTMLElement) => {
    return !isHidden(item);
  });
  return focusables;
};

function isElement(element: HTMLElement) {
  return element instanceof Element;
}

const focusOnElementOrNearestAncestor = (element: HTMLElement): void => {
  if (isElement(element)) {
    if (isTabbableElement(element)) {
      element.focus();
    } else {
      // If the drawer opener is not focusable, focus the nearest focusable ancestor.
      let nearestAncestor = element.parentElement;
      // In case the launcher was removed (e.g. drawer opener was a dropdown menu item)
      // focus ends on <body> which ancestor is <html>.
      // In this case stop looking for the nearest ancestor.
      while (
        nearestAncestor &&
        nearestAncestor.nodeName !== 'HTML' &&
        !isTabbableElement(nearestAncestor)
      ) {
        nearestAncestor = nearestAncestor.parentElement;
      }
      if (nearestAncestor) {
        nearestAncestor.focus();
      }
    }
  }
};

export {
  focusOn, // preventScroll safe focus setter
  focusWithin, // focus on first tabbable in a container or container itself if there is none
  focusOnEnd, // focus on last tabstop in a container
  focusOnStart, // focus on first tabstop in a container
  getActiveElement, // get active element from document parent of the node
  getAutofocusFocusables, // get elements with autofocus attribute
  getBodyElement, // get the body element from document parent of the node
  isTabbableElement, // uses tabbableQuery selector to determine if an element is tabbable
  allTabbableElements, // uses tabbableQuery selector to find all tabbable elements inside an element
  focusOnElementOrNearestAncestor
};
